// Pulse: a chain's launches as they happen, in three lists from the pulse channel: fresh launches,
// bonding curves close to graduating, and fresh graduations. Each list keeps its rows by address,
// applies the rows each message changed, and shows them in the message's order. Filters are the
// API's own Pulse filters, sent with the subscription, so a list holds only what matches.
import "./pulse.css";
import { useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { CHAIN_IDS, CHAINS, chainBySlug, tokenHref, useLive, useNow, type ChainId, type PulseToken } from "./api";
import { age, compact, pct, share, short, usd } from "./format";
import { type IconName } from "./icons";
import { A, ChainMark, Copy, Icon, Launchpad, SocialLinks } from "./ui";

type View = "new" | "graduating" | "graduated";
const COLUMNS: { view: View; name: string; hint: string; empty: string }[] = [
  { view: "new", name: "New pairs", hint: "Launches still on their bonding curve, newest first", empty: "No launch in the last 24 hours." },
  { view: "graduating", name: "Final stretch", hint: "Open curves that traded in the last hour, closest to graduating first", empty: "No open curve traded in the last hour." },
  { view: "graduated", name: "Migrated", hint: "Curves that completed in the last 24 hours, newest first", empty: "No curve completed in the last 24 hours." },
];

// ─── Filters ───

type Filters = {
  mcMin: string;
  mcMax: string;
  vol1hMin: string;
  tx1hMin: string;
  ageMax: string; // minutes
  devMax: string; // percent of supply
  snipersMax: string;
  has: string[];
  keywords: string;
  exclude: string;
};

const NO_FILTERS: Filters = { mcMin: "", mcMax: "", vol1hMin: "", tx1hMin: "", ageMax: "", devMax: "", snipersMax: "", has: [], keywords: "", exclude: "" };
const HAS = [
  ["logo", "Logo"],
  ["twitter", "X"],
  ["website", "Website"],
  ["telegram", "Telegram"],
  ["discord", "Discord"],
] as const;
const STORE = "aurelia-screener:pulse-filters";

const num = (s: string) => (s.trim() === "" || !Number.isFinite(Number(s)) ? null : Number(s));
const words = (s: string) => s.split(",").map((w) => w.trim()).filter(Boolean).join(",");
const round = (s: string) => s.replace(/\.0+(?=[KMBT]?$)/, ""); // $5.00K is $5K in a label
const range = (min: number | null, max: number | null) => (min == null && max == null ? null : `${min ?? ""}..${max ?? ""}`);

/** The filters as the API's Pulse parameters. */
function toParams(f: Filters): Record<string, string> {
  const p: Record<string, string | null> = {
    marketCapUsd: range(num(f.mcMin), num(f.mcMax)),
    "windows.1h.volumeUsd": range(num(f.vol1hMin), null),
    "windows.1h.trades": range(num(f.tx1hMin), null),
    age: range(null, num(f.ageMax) == null ? null : num(f.ageMax)! * 60),
    creatorNetShare: range(null, num(f.devMax) == null ? null : num(f.devMax)! / 100),
    sniperCount: range(null, num(f.snipersMax)),
    has: f.has.length ? f.has.join(",") : null,
    keywords: words(f.keywords) || null,
    excludeKeywords: words(f.exclude) || null,
  };
  return Object.fromEntries(Object.entries(p).filter((e): e is [string, string] => e[1] != null));
}

/** Each active filter as a chip label, with what clearing it leaves. */
function chips(f: Filters): { label: string; clear: Filters }[] {
  const out: { label: string; clear: Filters }[] = [];
  const mcMin = num(f.mcMin), mcMax = num(f.mcMax);
  if (mcMin != null || mcMax != null) {
    const label = mcMin != null && mcMax != null ? `MC ${round(usd(mcMin))} to ${round(usd(mcMax))}` : mcMin != null ? `MC from ${round(usd(mcMin))}` : `MC up to ${round(usd(mcMax))}`;
    out.push({ label, clear: { ...f, mcMin: "", mcMax: "" } });
  }
  if (num(f.vol1hMin) != null) out.push({ label: `Vol 1h from ${round(usd(num(f.vol1hMin)))}`, clear: { ...f, vol1hMin: "" } });
  if (num(f.tx1hMin) != null) out.push({ label: `Txns 1h from ${round(compact(num(f.tx1hMin)))}`, clear: { ...f, tx1hMin: "" } });
  if (num(f.ageMax) != null) out.push({ label: `Under ${num(f.ageMax)} min old`, clear: { ...f, ageMax: "" } });
  if (num(f.devMax) != null) out.push({ label: `Dev holds up to ${num(f.devMax)}%`, clear: { ...f, devMax: "" } });
  if (num(f.snipersMax) != null) out.push({ label: `Up to ${num(f.snipersMax)} snipers`, clear: { ...f, snipersMax: "" } });
  for (const h of f.has) out.push({ label: `Has ${HAS.find(([k]) => k === h)?.[1] ?? h}`, clear: { ...f, has: f.has.filter((x) => x !== h) } });
  if (words(f.keywords)) out.push({ label: `Name has ${words(f.keywords).replace(/,/g, " or ")}`, clear: { ...f, keywords: "" } });
  if (words(f.exclude)) out.push({ label: `Without ${words(f.exclude).replace(/,/g, ", ")}`, clear: { ...f, exclude: "" } });
  return out;
}

function loadFilters(): Filters {
  try {
    return { ...NO_FILTERS, ...JSON.parse(localStorage.getItem(STORE) ?? "{}") };
  } catch {
    return NO_FILTERS;
  }
}

function saveFilters(f: Filters) {
  try {
    localStorage.setItem(STORE, JSON.stringify(f));
  } catch {}
}

// ─── Page ───

export function Pulse() {
  const [chain, setChain] = useState<ChainId>(() => chainBySlug(new URLSearchParams(location.search).get("chain") ?? "") ?? "solana");
  const [launchpad, setLaunchpad] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(loadFilters);
  const [shown, setShown] = useState<View>("new");
  const dialog = useRef<HTMLDialogElement>(null);
  const now = useNow(1000);

  useEffect(() => void (document.title = "Pulse – Aurelia Screener"), []);

  const pickChain = (c: ChainId) => {
    setChain(c);
    setLaunchpad(null);
    history.replaceState(null, "", c === "solana" ? "/pulse" : `/pulse?chain=${CHAINS[c].slug}`);
  };
  const apply = (f: Filters) => {
    setFilters(f);
    saveFilters(f);
  };

  const active = chips(filters);
  const params = { ...toParams(filters), ...(launchpad ? { launchpad } : {}) };

  return (
    <div className="page pulse-page">
      <div className="pulse-toolbar">
        <div className="segmented" role="group" aria-label="Chain">
          {CHAIN_IDS.map((c) => (
            <button key={c} className={chain === c ? "seg active" : "seg"} onClick={() => pickChain(c)}>
              <ChainMark chain={c} size={14} />
              {CHAINS[c].name}
            </button>
          ))}
        </div>
        {CHAINS[chain].launchpads.length > 1 && (
          <div className="segmented" role="group" aria-label="Launchpad">
            {[null, ...CHAINS[chain].launchpads].map((l) => (
              <button key={l ?? "all"} className={launchpad === l ? "seg active" : "seg"} onClick={() => setLaunchpad(l)}>
                {l ? <Launchpad id={l} size={16} /> : "All"}
              </button>
            ))}
          </div>
        )}
        <button className={active.length ? "pulse-filter-button on" : "pulse-filter-button"} onClick={() => dialog.current?.showModal()}>
          <Icon name="filter" size={16} />
          Filters
          {active.length > 0 && <span className="pulse-filter-count num">{active.length}</span>}
        </button>
      </div>

      {active.length > 0 && (
        <div className="pulse-chips">
          {active.map((c) => (
            <button key={c.label} className="pulse-chip" onClick={() => apply(c.clear)} aria-label={`Remove the filter ${c.label}`}>
              {c.label}
              <span aria-hidden="true" className="pulse-chip-x">
                <Icon name="close" size={13} />
              </span>
            </button>
          ))}
          <button className="pulse-chip-reset" onClick={() => apply(NO_FILTERS)}>
            Clear all
          </button>
        </div>
      )}

      <div className="segmented pulse-switch" role="tablist" aria-label="List">
        {COLUMNS.map((c) => (
          <button key={c.view} role="tab" aria-selected={shown === c.view} className={shown === c.view ? "seg active" : "seg"} onClick={() => setShown(c.view)}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="pulse-columns">
        {COLUMNS.map((c) => (
          <Column key={c.view} chain={chain} column={c} params={params} filtered={Object.keys(params).length > 0} now={now} active={shown === c.view} />
        ))}
      </div>

      <FilterDialog ref={dialog} filters={filters} onApply={apply} />
    </div>
  );
}

// ─── A list ───

type List = { order: string[]; rows: Map<string, PulseToken>; total: number; fresh: Set<string> };

function Column({ chain, column, params, filtered, now, active }: { chain: ChainId; column: (typeof COLUMNS)[number]; params: Record<string, string>; filtered: boolean; now: number; active: boolean }) {
  const [list, setList] = useState<List | null>(null);
  const [error, setError] = useState<string | null>(null);
  // While the pointer is over a list it holds its order, so a new launch does not move the card
  // being clicked. Rows still update in place.
  const [frozen, setFrozen] = useState<string[] | null>(null);
  const seen = useRef(new Map<string, PulseToken>());
  const sub = { channel: "pulse", chain, view: column.view, limit: 30, ...params };
  const key = JSON.stringify(sub);

  useEffect(() => {
    setList(null);
    setError(null);
    setFrozen(null);
    seen.current.clear();
  }, [key]);

  useLive(sub, (m) => {
    if (m.type === "error") return setError(m.error ?? "the list could not be followed");
    if (m.type !== "pulse") return;
    const data = m.data as { order: string[]; tokens: PulseToken[]; total: number };
    setError(null);
    setList((prev) => {
      const rows = new Map(prev?.rows ?? []);
      const fresh = new Set<string>();
      for (const t of data.tokens) {
        // A row that joins after the first list is tinted once; the first list arrives all at once.
        if (prev && !rows.has(t.address)) fresh.add(t.address);
        rows.set(t.address, t);
        seen.current.set(t.address, t);
      }
      const listed = new Set(data.order);
      for (const a of rows.keys()) if (!listed.has(a)) rows.delete(a);
      if (seen.current.size > 300) for (const a of [...seen.current.keys()].slice(0, 100)) if (!listed.has(a)) seen.current.delete(a);
      return { order: data.order, rows, total: data.total, fresh };
    });
  });

  const order = frozen ?? list?.order ?? [];
  const row = (a: string) => list?.rows.get(a) ?? seen.current.get(a);

  return (
    <section className="pulse-col" data-active={active || undefined} aria-label={column.name}>
      <header className="pulse-col-head">
        <h2 title={column.hint}>{column.name}</h2>
        {frozen ? <span className="pulse-paused">Paused</span> : list && <span className="muted num">{list.total.toLocaleString("en-US")}</span>}
      </header>
      <div className="pulse-list" onPointerEnter={(e) => e.pointerType === "mouse" && list && setFrozen(list.order)} onPointerLeave={() => setFrozen(null)}>
        {error ? (
          <p className="pulse-note">This list could not be followed: {error}.</p>
        ) : !list ? (
          Array.from({ length: 6 }, (_, i) => <div key={i} className="pulse-row pulse-row-empty" />)
        ) : !order.length ? (
          <p className="pulse-note">{filtered ? "No launch matches these filters right now." : column.empty}</p>
        ) : (
          order.map((a) => {
            const t = row(a);
            return t ? <Row key={a} t={t} chain={chain} view={column.view} now={now} fresh={!frozen && list.fresh.has(a)} /> : null;
          })
        )}
      </div>
    </section>
  );
}

// ─── A launch ───

function Row({ t, chain, view, now, fresh }: { t: PulseToken; chain: ChainId; view: View; now: number; fresh: boolean }) {
  const h1 = t.windows["1h"];
  const m5 = t.windows["5m"].priceChange;
  const symbol = t.symbol ?? short(t.address);
  const since = view === "graduated" && t.graduatedAt ? t.graduatedAt : t.createdAt;
  const young = now - since < 60_000;
  const progress = view === "graduated" ? null : t.progress; // a migrated curve has nothing left to show
  const flow = h1.buys + h1.sells > 0 ? h1.buys / (h1.buys + h1.sells) : null;
  // A share of the supply outside 0 to 100% cannot be right: show none rather than a wrong one.
  const dev = t.creatorNetShare != null && Math.abs(t.creatorNetShare) <= 1 ? t.creatorNetShare : null;
  const snipers = t.sniperNetShare != null && t.sniperNetShare >= 0 && t.sniperNetShare <= 1 ? t.sniperNetShare : null;

  return (
    // The cover link spans the row under its other controls: an <a> cannot hold another <a>.
    <article className={fresh ? "pulse-row fresh" : "pulse-row"}>
      <A href={tokenHref(chain, t.address)} className="pulse-cover" title={`${symbol}: open the chart`}>
        <span className="pulse-sr">{symbol}</span>
      </A>

      <Avatar t={t} progress={progress} />

      <div className="pulse-main">
        <div className="pulse-title">
          <strong className="pulse-symbol">{symbol}</strong>
          <span className="pulse-name">{t.name}</span>
          <span className="pulse-lift">
            <Copy text={t.address} label="Copy the token address" />
          </span>
        </div>
        <div className="pulse-meta">
          <span className={young ? "pulse-age young num" : "pulse-age num"} title={view === "graduated" ? "Since it migrated" : "Since it launched"}>
            {age(since, now)}
          </span>
          <span className="pulse-pad">
            <Launchpad id={t.launchpad} size={16} bare />
          </span>
          <span className="pulse-lift">
            <SocialLinks socials={t.socials} />
          </span>
        </div>
        <div className="pulse-tags">
          {dev != null && (
            <Tag icon="dev" risk={dev >= 0.1} title="Dev: what the creator bought minus what it sold, as a share of the supply">
              {dev < 0 ? `-${share(-dev)}` : share(dev)}
            </Tag>
          )}
          {t.sniperCount != null && (
            <Tag icon="sniper" risk={(snipers ?? 0) >= 0.1} title="Snipers: wallets that bought in the launch block or the 4 after it, and what they still hold of the supply">
              {t.sniperCount}
              {snipers != null && snipers > 0 && <span className="muted">{share(snipers)}</span>}
            </Tag>
          )}
          {h1.buyers != null && h1.buyers > 0 && (
            <Tag icon="people" title="Buyers: distinct wallets that bought in the last hour">
              {compact(h1.buyers)}
            </Tag>
          )}
          {m5 != null && m5 !== 0 && (
            <Tag icon={m5 > 0 ? "trendUp" : "trendDown"} tone={m5 > 0 ? "up" : "down"} title="Price change over the last 5 minutes">
              {pct(m5)}
            </Tag>
          )}
        </div>
      </div>

      <div className="pulse-figures">
        <Figure label="MC" value={usd(t.marketCapUsd)} big />
        <Figure label="V" value={usd(h1.volumeUsd)} title="Volume in the last hour" />
        <div className="pulse-figure" title={`${compact(h1.buys)} buys and ${compact(h1.sells)} sells in the last hour`}>
          <span className="pulse-figure-label">TX</span>
          <span className="num">{compact(h1.trades)}</span>
        </div>
        {flow != null && (
          <span className="pulse-flow" aria-hidden="true">
            <span style={{ width: `${flow * 100}%` }} />
          </span>
        )}
      </div>
    </article>
  );
}

/** The token's image inside an arc that fills as its curve sells. */
function Avatar({ t, progress }: { t: PulseToken; progress: number | null }) {
  const [failed, setFailed] = useState(false);
  const p = progress == null ? 0 : Math.min(1, Math.max(0, progress));
  return (
    <span className="pulse-avatar" style={{ ["--p" as string]: p }}>
      <span className="pulse-avatar-img">
        {t.logo && !failed ? (
          <img src={t.logo} alt="" loading="lazy" onError={() => setFailed(true)} />
        ) : (
          <span className="pulse-avatar-letter">{(t.symbol ?? "?").replace(/^\$/, "").slice(0, 1).toUpperCase()}</span>
        )}
      </span>
      {progress != null && <span className="pulse-avatar-pct num">{share(p)}</span>}
    </span>
  );
}

function Tag({ children, icon, risk, tone, title }: { children: ReactNode; icon: IconName; risk?: boolean; tone?: "up" | "down"; title: string }) {
  return (
    <span className={`pulse-tag tag-${icon}${risk ? " risk" : ""}${tone ? ` ${tone}` : ""}`} title={title} aria-label={title.split(":")[0]}>
      <Icon name={icon} size={15} />
      {children}
    </span>
  );
}

function Figure({ label, value, big, title }: { label: string; value: string; big?: boolean; title?: string }) {
  return (
    <div className={big ? "pulse-figure big" : "pulse-figure"} title={title}>
      <span className="pulse-figure-label">{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

// ─── The filter dialog ───

function FilterDialog({ ref, filters, onApply }: { ref: RefObject<HTMLDialogElement | null>; filters: Filters; onApply: (f: Filters) => void }) {
  const [draft, setDraft] = useState(filters);
  const set = (k: keyof Filters) => (e: { target: { value: string } }) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const close = () => ref.current?.close();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onApply(draft);
    close();
  };

  return (
    <dialog
      ref={ref}
      className="pulse-dialog"
      onClick={(e) => e.target === ref.current && close()}
      // Each opening starts from the filters in force, not from an abandoned draft.
      onToggle={(e) => (e.currentTarget as HTMLDialogElement).open && setDraft(filters)}
    >
      <form onSubmit={submit} className="pulse-dialog-body">
        <header className="pulse-dialog-head">
          <h2>Filters</h2>
          <button type="button" className="icon-button" aria-label="Close" onClick={close}>
            <Icon name="close" size={18} />
          </button>
        </header>
        <p className="pulse-dialog-lede muted">They apply to all three lists. A launch missing a value (no market cap yet, say) does not match a range on it.</p>

        <div className="pulse-fields">
          <Field label="Market cap" hint="USD">
            <NumberInput placeholder="Min" prefix="$" value={draft.mcMin} onChange={set("mcMin")} />
            <NumberInput placeholder="Max" prefix="$" value={draft.mcMax} onChange={set("mcMax")} />
          </Field>
          <Field label="Volume, last hour" hint="USD">
            <NumberInput placeholder="Min" prefix="$" value={draft.vol1hMin} onChange={set("vol1hMin")} />
          </Field>
          <Field label="Trades, last hour">
            <NumberInput placeholder="Min" value={draft.tx1hMin} onChange={set("tx1hMin")} />
          </Field>
          <Field label="Age" hint="since launch">
            <NumberInput placeholder="Max" suffix="min" value={draft.ageMax} onChange={set("ageMax")} />
          </Field>
          <Field label="Dev holding" hint="net bought, of supply">
            <NumberInput placeholder="Max" suffix="%" value={draft.devMax} onChange={set("devMax")} />
          </Field>
          <Field label="Snipers" hint="bought in the first blocks">
            <NumberInput placeholder="Max" value={draft.snipersMax} onChange={set("snipersMax")} />
          </Field>
        </div>

        <fieldset className="pulse-has">
          <legend className="pulse-field-label">Must have</legend>
          <div className="pulse-has-options">
            {HAS.map(([k, name]) => {
              const on = draft.has.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  className={on ? "pulse-toggle on" : "pulse-toggle"}
                  onClick={() => setDraft((d) => ({ ...d, has: on ? d.has.filter((x) => x !== k) : [...d.has, k] }))}
                >
                  {k !== "logo" && <Icon name={k} size={14} />}
                  {name}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="pulse-fields two">
          <Field label="Name or symbol has" hint="any of">
            <input className="pulse-input text" value={draft.keywords} onChange={set("keywords")} placeholder="cat, dog" maxLength={100} />
          </Field>
          <Field label="Leave out" hint="any of">
            <input className="pulse-input text" value={draft.exclude} onChange={set("exclude")} placeholder="test" maxLength={100} />
          </Field>
        </div>

        <footer className="pulse-dialog-foot">
          <button type="button" className="pill ghost" onClick={() => setDraft(NO_FILTERS)}>
            Reset
          </button>
          <button type="submit" className="pill">
            Apply
          </button>
        </footer>
      </form>
    </dialog>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="pulse-field">
      <span className="pulse-field-label">
        {label}
        {hint && <span className="muted"> {hint}</span>}
      </span>
      <div className="pulse-field-inputs">{children}</div>
    </div>
  );
}

function NumberInput({ value, onChange, placeholder, prefix, suffix }: { value: string; onChange: (e: { target: { value: string } }) => void; placeholder: string; prefix?: string; suffix?: string }) {
  return (
    <label className="pulse-input">
      {prefix && <span className="muted">{prefix}</span>}
      <input type="number" inputMode="decimal" min={0} step="any" value={value} onChange={onChange} placeholder={placeholder} aria-label={placeholder} />
      {suffix && <span className="muted">{suffix}</span>}
    </label>
  );
}
